import React, { useEffect, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'
import styled from 'styled-components'

import { useClickOutside } from '../../../hooks/useClickOutside'
import { Badge } from '../../Badge'
import { Button } from '../../Button'
import { Flex } from '../../Flex'
import { Icon } from '../../Icons'
import { Image } from '../../Image'
import { SearchMenu } from '../../SearchMenu'
import { Text } from '../../Text'
import { PageBanner, PageBannerProps } from '../PageBanner'
import { StickyWrapper } from '../StickyWrapper'
import { LoginAsHeader } from '../LoginAsHeader'
import { useSessionStorage } from '../../../hooks'
import { ThemeWrapper } from '../../theme'
import { NotificationCounter } from '../NotificationCounter'

const IconsContainer = styled(Flex)`
  align-items: center;

  button {
    padding: 4px;
    margin-right: ${({ theme }) => theme.space[2]};
  }
`

const ScrollDownContainer = styled.div<{ zIndex: number }>`
  position: relative;
  transition: all 250ms ease-out;
  transform: translateY(-100%);
  z-index: ${({ zIndex }) => zIndex};

  &.exited {
    display: none;
  }

  &.entered {
    transform: translateY(0);
  }
`

const AdditionalSearchButton = styled.div`
  height: 40px;
  background: ${({ theme }) => theme.colors.primary};
`

const ExternalLinkText = styled(Text)`
  height: 40px;
  margin-left: auto;
  margin-right: auto;
`

const ExternalLink = styled.a`
  font-weight: bold;
  display: flex;
  align-items: center;
`

const StyledHeader = styled(Flex)`
  justify-content: space-between;
  height: 61px;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 5;
  background: ${({ theme }) => theme.colors.white};
`

const Brand = styled(Image)`
  display: block;
  max-height: 45px;
  margin-left: 16px;
`

const DownIcon = styled(Image)`
  margin-left: 5px;
`

const SearchWrapper = styled.div`
  z-index: 6;
  position: relative;
`

export type SearchFilter = {
  label: string
  identifier: string
  placeholder?: string
  countries?: CountryFilter[]
}

export type CountryFilter = {
  label: string
  countryCode: string
}

export enum SearchWidgetDisplayMode {
  WithFilters = 'WithFilters',
  WithoutFilters = 'WithoutFilters'
}

export type Link = {
  url: string
  text: string
  target?: string
}

export type DropDownLink = {
  header: string
  links: Link[]
}

export type NavigationLink = {
  linkModel?: Link
  dropdownLinksMenuModel?: DropDownLink
}

type Props = {
  navMenuItems: NavigationLink[]
  countryFilterEnabled: boolean
  defaultCountryFilter?: CountryFilter
  otherCountriesFilter?: CountryFilter[]
  searchWidgetDisplayMode?: 'WithFilters' | 'WithoutFilters'
  searchFilters: SearchFilter[]
  visible: boolean
  setMenuVisibility: React.Dispatch<React.SetStateAction<boolean>>
  externalSearchLink?: { text: string; url: string }
  loginAsLogout: () => void
  hasBriqBulkImportRole: boolean
  ImportHistoryLink: React.FunctionComponent
  AdditionalImportHistoryLink: React.FunctionComponent
  CartLink: React.FunctionComponent
  HomeLink: React.FunctionComponent
  onSearchClick: (params: { query: string; filter: string; countryFilter?: string }) => void
  loggedIn: boolean
  userName: string
  logoUrl: {
    url: string
    alt?: string
  }
  showBulkImportLinkAboveSearchButton: boolean | undefined
  showAsBulkImportButton: boolean | undefined
  countOfOrderItems: number
  loginUrl: string
  queryString: string
  showMulticolumnDropdown: boolean | undefined
  NotificationsLink: React.FunctionComponent
  countUnreadNotifications: number
  handleMarkAllNotificationsReadClick: () => void
  isOpenedOverride?: boolean
  showAiChatbotButton: boolean
  toggleAiChatbotWidget: () => void
} & PageBannerProps

const StyledSearchButton = styled(Button)`
  position: relative;
  color: white !important;
  background: ${({ theme }) => `${theme.colors.primary} !important`};
  width: 100%;
  border-radius: 0px !important;
  span {
    padding: 0px 10px;
  }
  img {
    padding: 0px 10px;
  }
`
const AiStyledButton = styled(Button)`
  padding-left: ${({ theme }) => theme.space[1]} !important;
  padding-right: ${({ theme }) => theme.space[1]} !important;
`

type State = {
  isSearchOpened: boolean
  isSearchOpenedOverride: boolean | undefined
  isMoreOptionsOpened: boolean
  lastStateChangeDate: number
}

function reducer(
  prevState: State,
  action: { type: 'OpenSearch' | 'OpenMoreOptions' | 'ToggleMoreOptions' | 'CollapseMoreOptions' | 'CollapseAll' }
): State {
  switch (action.type) {
    case 'OpenSearch':
      return { ...prevState, isSearchOpened: prevState.isSearchOpenedOverride ?? true, lastStateChangeDate: Date.now() }
    case 'OpenMoreOptions':
      return { ...prevState, isMoreOptionsOpened: true, lastStateChangeDate: Date.now() }
    case 'ToggleMoreOptions':
      return {
        ...prevState,
        isMoreOptionsOpened: prevState.isSearchOpened ? !prevState.isMoreOptionsOpened : false,
        lastStateChangeDate: Date.now()
      }
    case 'CollapseMoreOptions':
      return { ...prevState, isMoreOptionsOpened: false, lastStateChangeDate: Date.now() }
    case 'CollapseAll':
      return {
        ...prevState,
        isSearchOpened: prevState.isSearchOpenedOverride ?? false,
        isMoreOptionsOpened: false,
        lastStateChangeDate: Date.now()
      }
    default:
      return prevState
  }
}

const transitionChangeTimeMs = 200

const MobileHeaderComponent = ({
  searchFilters,
  visible,
  externalSearchLink,
  setMenuVisibility,
  searchWidgetDisplayMode,
  countryFilterEnabled,
  defaultCountryFilter,
  otherCountriesFilter,
  loginAsLogout,
  hasBriqBulkImportRole,
  ImportHistoryLink,
  AdditionalImportHistoryLink,
  CartLink,
  HomeLink,
  onSearchClick,
  loggedIn,
  userName,
  logoUrl,
  showBulkImportLinkAboveSearchButton,
  showAsBulkImportButton,
  countOfOrderItems,
  loginUrl,
  queryString,
  showMulticolumnDropdown,
  NotificationsLink,
  countUnreadNotifications,
  handleMarkAllNotificationsReadClick,
  isOpenedOverride,
  showAiChatbotButton,
  toggleAiChatbotWidget,
  ...rest
}: Props) => {
  const { t } = useTranslation()
  const [state, dispatch] = useReducer<typeof reducer>(reducer, {
    isSearchOpened: isOpenedOverride ?? false,
    isSearchOpenedOverride: isOpenedOverride,
    isMoreOptionsOpened: false,
    lastStateChangeDate: Date.now()
  })
  const { isSearchOpened, lastStateChangeDate, isMoreOptionsOpened } = state

  useEffect(() => {
    const listenerCallback = () => {
      if (Date.now() - lastStateChangeDate > transitionChangeTimeMs * 2) {
        dispatch({ type: 'CollapseAll' })
      }
    }

    window.addEventListener('scroll', listenerCallback)
    return () => window.removeEventListener('scroll', listenerCallback)
  }, [dispatch, lastStateChangeDate])

  const ref = useRef(null)
  useClickOutside(ref, () => {
    dispatch({ type: 'CollapseAll' })
  })
  const [loginAsValue] = useSessionStorage('isLoginAs', '')
  const isLoginAs = loginAsValue === 'true'

  return (
    <ThemeWrapper>
      <StickyWrapper ref={ref} visible={visible} position="sticky">
        {isLoginAs && <LoginAsHeader loggedIn={loggedIn} fullName={userName} logout={loginAsLogout} />}
        <PageBanner {...rest} />
        <StyledHeader>
          <HomeLink>
            <Brand src={logoUrl.url} alt={logoUrl.alt} />
          </HomeLink>
          <IconsContainer>
            {/* <Button variant="transparent">
            <ShoppingCartOutlined color="primary" fontSize="24px" />
          </Button> */}
            {showAiChatbotButton && (
              <AiStyledButton
                onClick={() => {
                  toggleAiChatbotWidget()
                }}
                icon={<Icon type="AiAgent" />}
                variant="primary"
              />
            )}
            {loggedIn && (
              <>
                <Button variant="transparent" onClick={() => setMenuVisibility(v => !v)}>
                  <Image type="userCheck" alt="user check" />
                </Button>
                <NotificationsLink>
                  <NotificationCounter
                    countUnreadNotifications={countUnreadNotifications}
                    handleMarkAllReadClick={handleMarkAllNotificationsReadClick}
                  />
                </NotificationsLink>
              </>
            )}
            {!loggedIn && (
              <a href={loginUrl}>
                <Button variant="transparent">
                  <Image type="userLoggedOut" alt="user logged out" />
                </Button>
              </a>
            )}
            <CartLink>
              <Button variant="transparent" overflow="visible !important">
                <Badge count={countOfOrderItems} offset={[3, -3]}>
                  <Image type="shoppingCart" alt="shopping cart" />
                </Badge>
              </Button>
            </CartLink>
            <Button variant="transparent" onClick={() => setMenuVisibility(v => !v)}>
              <Icon type="MenuOutlined" color="primary" fontSize="24px" />
            </Button>
          </IconsContainer>
        </StyledHeader>
        {!isSearchOpened && (
          <StyledSearchButton
            onClick={(e: React.ChangeEvent) => {
              dispatch({ type: 'OpenSearch' })
              e.stopPropagation()
            }}
          >
            {t('form:SearchForBusiness')}
            <Image type="searchWhite" alt="search" />
          </StyledSearchButton>
        )}
        {/* </StickyWrapper> */}
        <Transition
          in={isSearchOpened}
          timeout={{ appear: 0, enter: transitionChangeTimeMs, exit: transitionChangeTimeMs }}
        >
          {state => (
            <ScrollDownContainer zIndex={4} className={state}>
              <SearchWrapper className="mobile-header-wrapper">
                <SearchMenu
                  searchWidgetDisplayMode={searchWidgetDisplayMode}
                  searchOptions={searchFilters}
                  sticky
                  mobile
                  collapseSearch={() => {
                    dispatch({ type: 'CollapseAll' })
                  }}
                  showMoreBtn={{
                    text: (
                      <Text color="white">
                        {t('core:MoreOptions')}
                        <DownIcon type={isMoreOptionsOpened ? 'up' : 'down'} />
                      </Text>
                    ),
                    onClick: () => dispatch({ type: 'ToggleMoreOptions' })
                  }}
                  countryFilterEnabled={countryFilterEnabled}
                  defaultCountryFilter={defaultCountryFilter}
                  otherCountriesFilter={otherCountriesFilter}
                  onSearchClick={onSearchClick}
                  queryString={queryString}
                  loggedIn={loggedIn}
                  hasBriqBulkImportRole={hasBriqBulkImportRole}
                  showAsBulkImportButton={showAsBulkImportButton}
                  showBulkImportLinkAboveSearchButton={showBulkImportLinkAboveSearchButton}
                  ImportHistoryLink={ImportHistoryLink}
                  showMulticolumnDropdown={showMulticolumnDropdown}
                  popupContainerClassName=".mobile-header-wrapper"
                />
              </SearchWrapper>
              <Transition
                in={isMoreOptionsOpened}
                timeout={{ appear: 0, enter: transitionChangeTimeMs, exit: transitionChangeTimeMs }}
              >
                {state => (
                  <ScrollDownContainer className={state} zIndex={3}>
                    {externalSearchLink && (
                      <AdditionalSearchButton>
                        <ExternalLink href={externalSearchLink.url}>
                          <ExternalLinkText color="textPrimaryInverse">
                            {externalSearchLink.text}
                            <Image type="arrowRight2White" alt="arrow right" />
                          </ExternalLinkText>
                        </ExternalLink>
                      </AdditionalSearchButton>
                    )}
                    {loggedIn && hasBriqBulkImportRole && showBulkImportLinkAboveSearchButton && (
                      <AdditionalSearchButton>
                        <AdditionalImportHistoryLink>
                          <ExternalLinkText color="textPrimaryInverse">
                            {t('import:BulkImportLinkText')}
                            <Image type="arrowRight2White" alt="arrow right" />
                          </ExternalLinkText>
                        </AdditionalImportHistoryLink>
                      </AdditionalSearchButton>
                    )}
                  </ScrollDownContainer>
                )}
              </Transition>
            </ScrollDownContainer>
          )}
        </Transition>
      </StickyWrapper>
    </ThemeWrapper>
  )
}

export const MobilePageHeader = MobileHeaderComponent
export default MobilePageHeader
